
// NG CORE i18n
import { ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LOCALE_ID,  NgModule, APP_INITIALIZER } from '@angular/core';
import { registerLocaleData, DatePipe } from '@angular/common';
import { BrowserModule, Title } from '@angular/platform-browser';

// JWT
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { JwtInterceptor } from './_helpers';

// ngx-translate
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import localeEs from '@angular/common/locales/es';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

import { AppComponent } from './app.component';
import { routing } from './app.routing';

// toastr
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { PlusToast } from './plus.toast';

// infinite scroll
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

import { NgxSpinnerModule } from 'ngx-spinner';


// component
import { HomeComponent } from './home';
import { LoginComponent } from './header/login';
import { FooterComponent } from './footer/footer.component';
import { NewuserComponent } from './user/newuser/newuser.component';
import { HeaderComponent } from './header/header.component';
import { SlideComponent } from './header/slide/slide.component';
import { UserbarComponent } from './header/userbar/userbar.component';
import { ForgotpasswordComponent } from './user/forgotpassword/forgotpassword.component';
import { MarcComponent } from './marc/marc.component';
import { UserEditComponent } from './user/user/user-edit/user-edit.component';
import { UserNavComponent } from './user/user/user-nav/user-nav.component';
import { UserCardComponent } from './user/user/user-card/user-card.component';
import { UserComponent } from './user/user/user.component';
import { ProductListComponent } from './products/product-list/product-list.component';
import { UserValidator } from './_validators/user.validator';
import { UserAddressComponent } from './user/user/user-edit/user-address/user-address.component';
import { ZipCodeValidator } from './_validators/zipcode.validator';
import { ChangePasswordModalComponent } from './user/user/change-password-modal/change-password-modal.component';
import { ResetPasswordComponent } from './user/resetpassword/resetpassword.component';
import { UserUnsubscribeComponent } from './user/user/user-unsubscribe/user-unsubscribe.component';
import { UserAddressModalComponent } from './user/user/user-edit/user-address-modal/user-address-modal.component';
import { InfoCardModalComponent } from './user/info-card-modal/info-card-modal.component';
import { RgpdModalComponent } from './user/rgpd-modal/rgpd-modal.component';
import { UserValidateComponent } from './user/user-validate/user-validate.component';
import { ConfirmModalComponent } from './confirm-modal/confirm-modal.component';
import { SearchComponent } from './search/search.component';
import { CanDeactivateGuard } from './_guards/can-deactivate/can-deactivate.guard';
import { UserCouponsComponent } from './user/user/user-coupons/user-coupons.component';
import { UserCouponsModalComponent } from './user/user/user-coupons/user-coupons-modal/user-coupons-modal.component';
import { UserOrdersComponent } from './user/user/user-orders/user-orders.component';
import { CategoryNavComponent } from './header/category-nav/category-nav.component';
import { ProductsComponent } from './products/products/products.component';
import { InfoAppModalComponent } from './user/info-app-modal/info-app-modal.component';
import { UserAddressesComponent } from './user/user/user-addresses/user-addresses.component';
import { UserAppComponent } from './user/user/user-app/user-app.component'
import { AppLoadService } from './_services/app-load.service';
import { CatalogService, UtilsService } from './_services';
import { OffcanvasComponent } from './offcanvas/offcanvas.component';
import { Error404Component } from './static-pages/error404/error404.component';
import { ProductDetailComponent } from './products/product-detail/product-detail.component';
import { environment } from 'src/environments/environment.prod';
import { ProductExtendedInfoModalComponent } from './products/product-extended-info-modal/product-extended-info-modal.component';
import { ProductFormatOptionsModalComponent } from './products/product-format-options-modal/product-format-options-modal.component';
import { ProductAddComponent } from './products/product-add/product-add.component';
import { ProductPriceComponent } from './products/product-price/product-price.component';
import { BasketWidgetComponent } from './header/userbar/basket-widget/basket-widget.component';
import { UserCartComponent } from './user/user-cart/user-cart.component';
import { ProductInfoComponent } from './products/product-detail/product-info/product-info.component';
import { ProductDetailModalComponent } from './products/product-detail-modal/product-detail-modal.component';
import { CurrentCouponsComponent } from './user/user/user-coupons/current-coupons/current-coupons.component';
import { CartPaymentErrorComponent } from './user/user-cart/cart-payment-error/cart-payment-error.component';
import { UserListComponent } from './user/user/user-list/user-list.component';
import { NewListModalComponent } from './user/user/user-list/new-list-modal/new-list-modal.component';
import { AddToListComponent } from './products/add-to-list/add-to-list.component';
import { CartPaymentSuccessComponent } from './user/user-cart/cart-payment-success/cart-payment-success.component';
import { MessageModalComponent } from './message-modal/message-modal.component';
import { PrivacyPolicyComponent } from './static-pages/privacy-policy/privacy-policy.component';
import { LegalWarningComponent } from './static-pages/legal-warning/legal-warning.component';
import { CookiePolicyComponent } from './static-pages/cookie-policy/cookie-policy.component';
import { BagsModalsComponent } from './user/user/bags-modals/bags-modals.component';
import { PurchaseConditionsComponent } from './static-pages/purchase-conditions/purchase-conditions.component';
import { HouseorshopModalComponent } from './user/user/houseorshop-modal/houseorshop-modal.component';
import { HowToBuyComponent } from './static-pages/how-to-buy/how-to-buy.component';
import { FaqsComponent } from './static-pages/faqs/faqs.component';
import { PaymentMethodsComponent } from './static-pages/payment-methods/payment-methods.component';
import { DeliveryZonesComponent } from './static-pages/delivery-zones/delivery-zones.component';
import { Add2ListModalComponent } from './user/user/user-list/add2-list-modal/add2-list-modal.component';
import { BasketFromListModalComponent } from './user/user/user-list/basket-to-list-modal/basket-to-list-modal.component';
import { EditListNameModalComponent } from './user/user/user-list/edit-list-name-modal/edit-list-name-modal.component';
import { VariableWeightModalsComponent } from './user/user/variableWeight-modals/variableWeight-modals.component';
import { PromosComponent  } from './promos/promos/promos.component';
import { PromoDetailComponent } from './promos/promo-detail/promo-detail.component';
import { ProductBatchModalComponent } from './products/product-batch-modal/product-batch-modal.component';
import { WebPromosComponent } from './products/web-promos/web-promos.component';
//20250602 axa inici
import { DeliveryAxaComponent } from './header/delivery-axa/delivery-axa.component';
import { AxaBarComponent } from './header/axa-bar/axa-bar.component';
import { ModalModalitiesComponent } from './header/modal-modalities/modal-modalities.component';
import { ModalInfoAXAComponent } from './header/modal-info-axa/modal-info-axa.component';
import { ModalInfoAXA2Component } from './header/modal-info-axa2/modal-info-axa2.component';
import { LoginAxaErrorModalComponent } from './login-axa-error-modal/login-axa-error-modal.component';
//20250602 axa final

export function init_app(appLoadService: AppLoadService) {
  return () => appLoadService.initializeApp();
}

registerLocaleData(localeEs, 'es');

@NgModule({
  imports: [
    NgbModule,
    BrowserModule,
    ReactiveFormsModule,
    HttpClientModule,
    routing,
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),
    BrowserAnimationsModule, 
    ToastrModule.forRoot({
      //timeOut: 0,
      timeOut: environment.MESSAGES_TIMEOUT,
      positionClass: 'toast-bottom-left',
      preventDuplicates: true,
      newestOnTop: true,
      progressBar: false,
      enableHtml : true,
      toastComponent: PlusToast,
      toastClass: 'toastPlus',
    }),
    InfiniteScrollModule,
    NgxSpinnerModule
  ],
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    FooterComponent,
    NewuserComponent,
    HeaderComponent,
    SlideComponent,
    UserValidateComponent,
    UserbarComponent,
    ForgotpasswordComponent,
    MarcComponent,
    UserEditComponent,
    UserNavComponent,
    UserCardComponent,
    UserComponent,
    ProductListComponent,
    UserAddressComponent,
    ChangePasswordModalComponent,
    ResetPasswordComponent,
    UserUnsubscribeComponent,
    UserAddressModalComponent,
    InfoCardModalComponent,
    RgpdModalComponent,
    ConfirmModalComponent,
    SearchComponent,
    UserCouponsComponent,
    UserCouponsModalComponent,
    UserOrdersComponent,
    CategoryNavComponent,
    ProductsComponent,
    InfoAppModalComponent,
    UserAddressesComponent,
    UserAppComponent,
    OffcanvasComponent,
    Error404Component,
    ProductDetailComponent,
    ProductExtendedInfoModalComponent,
    ProductFormatOptionsModalComponent,
    ProductAddComponent,
    ProductPriceComponent,
    BasketWidgetComponent,
    UserCartComponent,
    ProductInfoComponent,
    PlusToast,
    ProductDetailModalComponent,
    CurrentCouponsComponent,
    CartPaymentErrorComponent,
    UserListComponent,
    NewListModalComponent,
    AddToListComponent,
    CartPaymentSuccessComponent,
    MessageModalComponent,
    PrivacyPolicyComponent,
    LegalWarningComponent,
    CookiePolicyComponent,
    BagsModalsComponent,
    PurchaseConditionsComponent,
    HouseorshopModalComponent,
    HowToBuyComponent,
    FaqsComponent,
    PaymentMethodsComponent,
    DeliveryZonesComponent,
    Add2ListModalComponent,
    BasketFromListModalComponent,
    EditListNameModalComponent,
    VariableWeightModalsComponent,
    PromosComponent,
    PromoDetailComponent,
    ProductBatchModalComponent,
    WebPromosComponent,
    //20250602 axa inici
    DeliveryAxaComponent,
    AxaBarComponent,
    ModalModalitiesComponent,
    ModalInfoAXAComponent,
    ModalInfoAXA2Component,
    LoginAxaErrorModalComponent,
    //20250602 axa final

  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: init_app, deps:  [AppLoadService] , multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'es' },
    AppLoadService,
    UtilsService,
    UserValidator,
    ZipCodeValidator,
    Title,
    DatePipe ,
    CanDeactivateGuard,
  ],
  entryComponents: [
    PlusToast
  ],
  bootstrap: [AppComponent]
})

export class AppModule {  }
